import { useEffect, useRef } from 'react';
import DocumentTitle from 'react-document-title';
import { useLocation, useParams } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Loader } from '@hivebrite/design-system/Loader';
import { PillsList } from '@hivebrite/design-system/PillsList';

import AttachmentsSlider from 'hb-react/frontoffice/apps/Forum/components/AttachmentsSlider';
import DetailsLayout from 'hb-react/frontoffice/apps/Forum/components/DetailsLayout';
import DiscussionContent from 'hb-react/frontoffice/apps/Forum/components/DiscussionContent';
import DiscussionNotFound from 'hb-react/frontoffice/apps/Forum/components/DiscussionNotFound';
import DiscussionReactions from 'hb-react/frontoffice/apps/Forum/components/DiscussionReactions';
import DiscussionUserAndDropdown from 'hb-react/frontoffice/apps/Forum/components/DiscussionUserAndDropdown';
import { COMMENTS_SCROLL } from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { useGetDiscussion } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useGetDiscussion';
import type { Discussion } from 'hb-react/frontoffice/apps/Forum/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

const ForumDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { currentNetwork } = useDefaultAppContexts();
  const { topicName } = useForumContext();
  const { discussion, isLoading, isError } = useGetDiscussion(
    parseInt(id!, 10),
  );

  if (isLoading) {
    return (
      <DetailsLayout>
        <Loader size={32} />
      </DetailsLayout>
    );
  }

  if (isError || !discussion) {
    return (
      <DetailsLayout>
        <DiscussionNotFound />
      </DetailsLayout>
    );
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: discussion.title,
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName,
        })}
      />
      <DetailsPage discussion={discussion} />;
    </>
  );
};

interface DetailsPageProps {
  discussion: Discussion;
}

export const DetailsPage = ({ discussion }: DetailsPageProps) => {
  const reactionsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { configUrls } = useForumContext();
  const { categories, storage_attachments: storageAttachments } = discussion;

  useEffect(() => {
    if (location.state === COMMENTS_SCROLL) {
      reactionsRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <DetailsLayout>
      <Card
        px={{ _: 4, m: 11, l: '82px', xl: '116px' }}
        pb={{ _: 0, m: 8 }}
        noBorderRadiusOnSmallScreen
      >
        <DiscussionUserAndDropdown
          discussion={discussion}
          configUrls={configUrls}
        />
        <Box display="block" mb={5}>
          <DiscussionContent discussion={discussion} />
        </Box>
        {categories && categories.length > 0 && (
          <Box mb={5}>
            <PillsList
              pills={categories}
              seeLessLabel={s__('Forum|See less')}
              seeMoreLabel={s__('Forum|See all')}
            />
          </Box>
        )}
        {storageAttachments && storageAttachments.length > 0 && (
          <AttachmentsSlider storageAttachments={storageAttachments} />
        )}
        <Box display="block" mt={5} ref={reactionsRef}>
          <DiscussionReactions discussion={discussion} />
        </Box>
      </Card>
    </DetailsLayout>
  );
};

export default ForumDetails;
